import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	ViewChild,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ICON } from '@core/util/icon';
import { JurisdictionDepartments } from '@models/jurisdictions';
import {
	RequisitionPrecedent,
	JurisdictionDepartmentTypeField,
	MatterRequisitionPrecedent,
	MatterRequisitionPrecedentStatuses,
} from '@models/requisitions';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { BusinessRequisitionsState } from '@state/business/requisition/business_requisitions.state';
import { MatterRequisitionsState } from '@state/matter/requisitions/matter-requisitions.state';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { NewRequisitionModalComponent } from '../../modals/matter-requisition/new-requisition-modal/new-requisition-modal.component';
import { FormControlStatus, NgForm } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
	isCloserDefaultRequisition,
	isRequisitionPrecedentId,
} from '@globalUtils/requisition/requisition';
import { UpdateRequisitionModalComponent } from '../../modals/matter-requisition/update-requisition-modal/update-requisition-modal.component';
import { TestHooks } from './requisition-precedent-form.hooks';

enum OnTitleToggle {
	ALL = 'All',
	ON_TITLE = 'On-Title',
	OFF_TITLE = 'Off-Title',
}

@UntilDestroy()
@Component({
	selector: 'app-requisition-precedent-form',
	templateUrl: './requisition-precedent-form.component.html',
	styleUrls: ['./requisition-precedent-form.component.scss'],
	standalone: false,
})
export class RequisitionPrecedentFormComponent implements AfterViewInit {
	testHooks = TestHooks;
	@ViewChild('form') form: NgForm;
	@SelectSnapshot(MatterRequisitionsState.getAll)
	matterRequisitions: MatterRequisitionPrecedent[];
	@Select(BusinessRequisitionsState.getAll) requisitions$: Observable<
		RequisitionPrecedent[]
	>;
	@Input() public searchTerm: string = '';
	@Input() public set teranetFields(fields: Record<string, string>) {
		if (!fields) return;
		for (const [key, value] of Object.entries(fields)) {
			this.fieldValuePlaceholder.set(key, value);
		}

		if (this.selectedRequisition) {
			this.selectedRequisition = this.setRequisitionFields(
				this.selectedRequisition,
			);
		}
	}
	@Input() set selectedRequisitionPrecedent(
		selectedRequisitionPrecedent: MatterRequisitionPrecedent,
	) {
		if (!selectedRequisitionPrecedent) return;
		this.selectedRequisition = this.setRequisitionFields(
			selectedRequisitionPrecedent,
		);
		if (!this.selectedRequisition.defaultRequisitionId) {
			this.selectedRequisition.defaultRequisitionId = isRequisitionPrecedentId(
				selectedRequisitionPrecedent.id,
			)
				? selectedRequisitionPrecedent.id
				: selectedRequisitionPrecedent.defaultRequisitionId;
		}
		if (!this.selectedRequisition.status) {
			this.selectedRequisition.status =
				MatterRequisitionPrecedentStatuses.ACCEPT_UNDERTAKING;
		}
	}

	@Output() public selectedRequisitionPrecedentChange =
		new EventEmitter<MatterRequisitionPrecedent>();
	@Output() public onSubmit = new EventEmitter<MatterRequisitionPrecedent>();
	@Output() public formStatusChange = new EventEmitter<FormControlStatus>();

	public ICON = ICON;
	public titleFilter = OnTitleToggle.ALL;
	public OnTitleRadioGroup = Object.values(OnTitleToggle).map((value) => ({
		value,
		label: value,
		icon: value === this.titleFilter ? ICON.CIRCLE_CHECK : null,
	}));
	public selectedRequisition: MatterRequisitionPrecedent;
	// Memoize the values between requisition precedent changes, so user doesn't constantly have to re-enter values
	public fieldValuePlaceholder: Map<string, string | number> = new Map();

	constructor(
		private dialogRef: MatDialogRef<
			NewRequisitionModalComponent | UpdateRequisitionModalComponent,
			string
		>,
		private el: ElementRef,
	) {}

	ngAfterViewInit(): void {
		this.form.statusChanges
			.pipe(untilDestroyed(this))
			.subscribe((status) =>
				this.formStatusChange.emit(
					this.selectedRequisition ? status : 'INVALID',
				),
			);
		this.el.nativeElement
			?.querySelector(
				'.requisition-precedent-form__body__requisition-button_selected',
			)
			?.scrollIntoView({ behavior: 'auto', block: 'center' });
	}

	_onSubmit(form: NgForm) {
		if (!form.valid) return;

		this.onSubmit.emit({
			...this.selectedRequisition,
			order:
				this.selectedRequisition.order ??
				(this.matterRequisitions.length
					? this.matterRequisitions.at(-1).order + 1
					: 0),
		});
	}

	/**
	 *
	 * @param selection value from either a dropdown or an input
	 * @param field RequisitionField that has changed
	 * @description updates the value of the field in the selectedRequisition, we must create a new reference to the selectedRequisition
	 * in order to trigger change detection for the text areas.
	 */

	onFormInputSelect(
		selection:
			| DropdownResult<DropdownOption<string | number>>
			| string
			| number,
		field: Record<string, string>,
	) {
		if (typeof selection === 'string' || typeof selection === 'number') {
			this.selectedRequisition.fields[field.key].value = selection;
			this.fieldValuePlaceholder.set(field.key, selection);
		} else {
			this.selectedRequisition.fields[field.key].value = selection.result.value;
			this.fieldValuePlaceholder.set(field.key, selection.result.value);
		}
		this.selectedRequisition = { ...this.selectedRequisition };
	}

	onRequisitionSelect(selectedRequisitionPrecedent: RequisitionPrecedent) {
		// remove custom requisition lastEditedFields
		const selectedRequisition = {
			...this.setRequisitionFields(selectedRequisitionPrecedent),
			lastEditedBy: null,
			lastEditedOn: null,
			lastEditedById: null,
		};

		this.selectedRequisition = {
			...this.selectedRequisition,
			...selectedRequisition,
			status:
				this.selectedRequisition?.status ??
				MatterRequisitionPrecedentStatuses.ACCEPT_UNDERTAKING,
			defaultRequisitionId: isCloserDefaultRequisition(selectedRequisition)
				? selectedRequisition.id
				: selectedRequisition.defaultRequisitionId,
		};
		this.selectedRequisitionPrecedentChange.emit(this.selectedRequisition);
	}

	closeModal() {
		this.dialogRef.close();
	}

	filterFunction(
		requisition: RequisitionPrecedent,
		searchTerm: string,
		titleFilterStatus: OnTitleToggle,
	) {
		if (titleFilterStatus === OnTitleToggle.ON_TITLE && !requisition.isOnTitle)
			return false;
		if (titleFilterStatus === OnTitleToggle.OFF_TITLE && requisition.isOnTitle)
			return false;
		if (
			searchTerm &&
			!requisition.name.toLowerCase().includes(searchTerm.toLowerCase())
		)
			return false;
		return true;
	}

	trackByRequisitionId(_index: number, requisition: RequisitionPrecedent) {
		return requisition.id;
	}

	private setRequisitionFields<
		T extends RequisitionPrecedent | MatterRequisitionPrecedent,
	>(selectedRequisitionPrecedent: T): T {
		const selectedRequisition = cloneDeep(selectedRequisitionPrecedent);

		for (const [key, requisitionFieldData] of Object.entries(
			selectedRequisition.fields,
		)) {
			if (!this.fieldValuePlaceholder.has(key)) {
				// Sets the default for the dropdown field departmentType for UTILITY_SEARCH, if it is not set (from new requisition)
				if (
					key === JurisdictionDepartmentTypeField.name &&
					requisitionFieldData.value === ''
				) {
					this.fieldValuePlaceholder.set(key, JurisdictionDepartments.TAX);
					selectedRequisition.fields[key].value = JurisdictionDepartments.TAX;
				} else {
					this.fieldValuePlaceholder.set(key, requisitionFieldData.value);
				}
			} else {
				selectedRequisition.fields[key].value =
					this.fieldValuePlaceholder.get(key);
			}
		}
		return selectedRequisition;
	}
}
